/**
 * Storefront header data hooks shared by the marketplace home layouts (MarketplaceHome's Amazon / Flipkart
 * header and BlinkitHome). Moved here verbatim from MarketplaceHome.jsx so both can use them without a
 * circular import.
 */
import { useEffect, useState } from "react";
import { api } from "./api";
import { useAuth } from "../contexts/AuthContext";
import { useChimeOnIncrease } from "./notifSound";

/** Live unread-notification count — polls every 60s while mounted and
 * also refreshes when the NotificationsPage dispatches
 * ``notifications:update``. Returns 0 for anonymous users, and ``null``
 * (not a false "0") for a logged-in user until the first fetch resolves
 * — this component fully remounts on every tab switch, and reporting a
 * placeholder 0 there made every fresh mount with pre-existing unread
 * items look like a brand-new increase to useChimeOnIncrease, chiming
 * on plain navigation (user-reported). ``IconBadgeButton`` below already
 * treats any falsy/null count as "don't show the badge", so this is
 * safe for the visible UI too. */
export function useUnreadNotifCount() {
    const { user } = useAuth();
    const [count, setCount] = useState(null);
    useEffect(() => {
        if (!user) { setCount(0); return; }
        let alive = true;
        let timer = null;
        const fetch = async () => {
            try {
                const { data } = await api.get("/notifications/unread-count");
                if (!alive) return;
                setCount(Number(data?.count || 0));
            } catch { /* ignore */ }
        };
        fetch();
        timer = setInterval(fetch, 60_000);
        const onUpdate = () => fetch();
        window.addEventListener("notifications:update", onUpdate);
        return () => {
            alive = false;
            if (timer) clearInterval(timer);
            window.removeEventListener("notifications:update", onUpdate);
        };
    }, [user]);
    // Chime on count increase so customers hear in-app push arrivals
    // even when the OS-level FCM sound fails (custom ROMs, DND profiles,
    // foreground state on iOS without time-sensitive entitlement, etc).
    useChimeOnIncrease(count);
    return count;
}

/** Subscribe to the user's default address and surface it on the
 * marketplace header. Updates on the global ``addresses:update`` event
 * that AddressPickerModal dispatches. */
export function useDefaultAddress() {
    const { user } = useAuth();
    const [addr, setAddr] = useState(null);
    useEffect(() => {
        if (!user) { setAddr(null); return; }
        let alive = true;
        const fetch = async () => {
            try {
                const { data } = await api.get("/addresses");
                if (!alive) return;
                const list = Array.isArray(data) ? data : [];
                setAddr(list.find((a) => a.is_default) || list[0] || null);
            } catch { /* ignore */ }
        };
        fetch();
        const onUpdate = () => fetch();
        window.addEventListener("addresses:update", onUpdate);
        return () => { alive = false; window.removeEventListener("addresses:update", onUpdate); };
    }, [user]);
    return addr;
}
